/**
 * 작성창의 '수신 그룹' 버튼 — 저장해 둔 묶음으로 받는이·참조·숨은참조를 한 번에 채운다.
 *
 * 적용 규칙 (세 줄)
 *   1. 덮어쓰지 않고 <b>더한다</b>. 이미 친 주소를 그룹이 지우면 되돌릴 수 없는 사고다.
 *   2. 같은 주소가 이미 있으면 <b>건드리지 않는다</b> — 역할이 달라도. 사용자가 직접 넣은 자리가
 *      그룹 규칙보다 우선한다(그래야 그룹을 눌러도 방금 한 조작이 뒤집히지 않는다).
 *   3. 넣지 못한 건 <b>반드시 말한다</b>. 조용히 빼면 사용자는 다 들어간 줄 알고 보낸다.
 *
 * 확인 대화상자를 두지 않는 이유
 *   되돌리기가 쉽다 — 칩을 지우면 그만이다. 되돌리기 쉬운 조작에 확인을 붙이면 매번 두 번 누르게 된다.
 */
import React, { useState } from "react";
import { Box, Divider, Menu, MenuItem, Typography } from "@mui/material";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";

import { roleSummary, toAddress } from "./contactGroupAddress";
import ActionButton from "../../../../component/common/ActionButton";
import { mailApi }  from "../../api/mailApi";
import { notify }   from "../../../../util/toast";

/** 입력칸 값 배열 → 소문자 이메일 Set. 중복 판정 기준을 한 곳에 둔다. */
function emailSet(...lists) {
    const set = new Set();
    for (const list of lists) {
        for (const v of list || []) {
            const a = toAddress(v);
            if (a) set.add(a.email.toLowerCase());
        }
    }
    return set;
}

/**
 * @param tos,ccs,bccs   현재 작성창 값 — 중복 판정에 쓴다.
 * @param onApply        ({ tos, ccs, bccs }) 로 <b>추가할 항목만</b> 넘긴다. 병합은 호출부가 한다
 *                       (여기서 통째로 만들어 넘기면 그 사이 사용자가 친 값과 경합한다).
 * @param sx             버튼 sx 덧입힘 — 주소 입력 행 안에 같은 줄로 앉힐 때 높이를 줄이는 용도.
 */
export default function ContactGroupPickerButton({ tos, ccs, bccs, onApply, sx }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [groups, setGroups]     = useState([]);
    const [loading, setLoading]   = useState(false);
    const [applying, setApplying] = useState(false);

    const openMenu = async (e) => {
        setAnchorEl(e.currentTarget);
        setLoading(true);
        try {
            const res = await mailApi.listContactGroups();
            const data = res?.data ?? res;
            setGroups(Array.isArray(data) ? data : []);
        } catch {
            setGroups([]);
            notify("수신 그룹을 불러오지 못했습니다", "error");
        } finally { setLoading(false); }
    };

    const handlePick = async (g) => {
        setApplying(true);
        try {
            const res = await mailApi.getContactGroup(g.id);
            const members = (res?.data ?? res)?.members || [];
            if (members.length === 0) {
                notify(`'${g.name}' 에는 수신자가 없습니다`, "warning");
                return;
            }

            // 이미 어느 칸에든 들어있는 주소는 건너뛴다. seen 을 루프 안에서도 키워야
            // 그룹 안에 같은 주소가 두 번 있을 때 두 칸에 나눠 들어가지 않는다.
            const seen = emailSet(tos, ccs, bccs);
            const add = { tos: [], ccs: [], bccs: [] };
            let skipped = 0;

            for (const m of members) {
                const a = toAddress(m);
                if (!a) { skipped++; continue; }
                const key = a.email.toLowerCase();
                if (seen.has(key)) { skipped++; continue; }
                seen.add(key);

                const item = a.name ? { name: a.name, email: a.email } : a.email;
                if (m.role === "CC")       add.ccs.push(item);
                else if (m.role === "BCC") add.bccs.push(item);
                else                       add.tos.push(item);
            }

            const added = add.tos.length + add.ccs.length + add.bccs.length;
            if (added === 0) {
                notify(`'${g.name}' 의 ${members.length}명은 이미 모두 들어가 있습니다`, "info");
                return;
            }

            onApply(add);

            // 어느 칸에 몇 명이 들어갔는지 그대로 말한다 — "적용했습니다" 만 띄우면
            // 참조에 들어갔는지 받는이에 들어갔는지를 사용자가 다시 세어 봐야 한다.
            const parts = [];
            if (add.tos.length)  parts.push(`받는이 ${add.tos.length}명`);
            if (add.ccs.length)  parts.push(`참조 ${add.ccs.length}명`);
            if (add.bccs.length) parts.push(`숨은참조 ${add.bccs.length}명`);
            notify(
                skipped > 0
                    ? `${parts.join(", ")}을 넣었습니다. ${skipped}명은 이미 있어 건너뛰었습니다.`
                    : `${parts.join(", ")}을 넣었습니다.`,
                skipped > 0 ? "warning" : "success");
        } catch (e) {
            notify(e?.response?.data?.message || "그룹을 적용하지 못했습니다", "error");
        } finally {
            setApplying(false);
            setAnchorEl(null);
        }
    };

    return (
        <>
            <ActionButton
                variant="secondary"
                startIcon={<GroupAddRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={openMenu}
                loading={applying}
                label="수신 그룹"
                tooltip="저장해 둔 묶음으로 받는이·참조·숨은참조를 한 번에 채웁니다"
                sx={sx}
            />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { minWidth: 260, maxWidth: 360, maxHeight: 380 } } }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.3 }}>
                        메일 수신 그룹
                    </Typography>
                </Box>
                <Divider />

                {loading ? (
                    <MenuItem disabled sx={{ fontSize: 13 }}>불러오는 중…</MenuItem>
                ) : groups.length === 0 ? (
                    // 여기서 만들게 하지 않는 이유: 작성 중인 메일 위에 편집 모달을 겹쳐 띄우면
                    // 어느 쪽이 활성인지 헷갈리고, 실수로 작성창을 닫으면 본문을 잃는다.
                    <Box sx={{ px: 2, py: 2 }}>
                        <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                            저장된 그룹이 없습니다.
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.5 }}>
                            연락처 &gt; 메일 수신 그룹에서 만들 수 있습니다.
                        </Typography>
                    </Box>
                ) : groups.map(g => (
                    <MenuItem key={g.id} onClick={() => handlePick(g)}
                              sx={{ display: "block", py: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }} noWrap>
                            {g.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: "#2563eb" }} noWrap>
                            {roleSummary(g)}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
